import { computed, makeObservable, toJS } from 'mobx';
import { JobFormManager } from '../job-form.manager';
import {
   CurrencyKey,
   ExperienceKey,
   JobCategory,
   JobSkill,
   WorktypeKey,
} from 'jobs/types';
import { v4 as uuidv4 } from 'uuid';
export class JobViewStore {
   constructor(private readonly jobManager: JobFormManager) {
      makeObservable(this, {
         attr: computed,
         jobTitle: computed,
         companyName: computed,
         logoSrc: computed,
         website: computed,
         jobDescription: computed,
         categories: computed,
         subCategbories: computed,
         workType: computed,
         salary: computed,
         languages: computed,
         jobLocation: computed,
         experience: computed,
      });
   }
   get attr() {
      return this.jobManager.jobAttributes.get();
   }

   get jobLocation(): string {
      const id = this.jobManager.form.controls['country'].value;
      const cityId = this.jobManager.form.controls['city'].value;
      let result = '';
      if (id && this.attr) {
         result += this.attr.countries.find((c) => c.id === Number(id))?.name ?? '';
         if (cityId) {
            const city = this.jobManager.locationVm.cities?.find((c) => c.id === Number(cityId));
            result += `, ${city?.name ?? ''}, ${city?.region ?? ''}`;
         }
      }
      return result;
   }

   get experience(): string {
      const valueKey = this.jobManager.form.controls['experience'].value as ExperienceKey;
      if (this.attr && valueKey) {
         return this.attr.experience[valueKey];
      }
      return '';
   }

   get jobTitle(): string {
      return this.jobManager.form.controls['job_title'].value;
   }

   get website(): string {
      return this.attr?.jobData.company_website || '#';
   }

   get companyName(): string {
      return this.attr?.jobData.company_name || '';
   }

   get logoSrc(): string {
      return this.attr?.jobData.company_logo || '/Images/jobseekerSide/dummy-profile.png';
   }

   get jobDescription(): string {
      return this.jobManager.form.controls['jobDescription'].value;
   }

   get skills(): JobSkill[] {
      return this.jobManager.skillsVm.multiselect.values;
   }

   get categories(): JobCategory[] {
      if (this.attr) {
         const values = [
            this.jobManager.form.controls['category'].value,
            this.jobManager.form.controls['category_1'].value,
            this.jobManager.form.controls['category_2'].value,
         ].filter((v) => !!v);

         if (values.length > 0) {
            return this.attr.categories.filter((cat) => values.includes(cat.id.toString()));
         } else {
            return [];
         }
      }
      return [];
   }

   get subCategbories(): JobCategory[] {
      const result = this.jobManager.categoriesGroup.categoriesGroup.flatMap((c) =>
         toJS(c.subCategory.multiselect.values)
      );
      return result;
   }

   get workType(): string {
      const workTypeId = this.jobManager.form.controls['work_type'].value as WorktypeKey;
      if (this.attr) {
         return this.attr.worktype[workTypeId];
      }
      return '';
   }

   get salary(): string {
      const min = this.jobManager.form.controls['salary_min'].value;
      const max = this.jobManager.form.controls['salary_max'].value;
      const currency = this.jobManager.form.controls['salary_currency'].value as CurrencyKey;
      if (min && max && currency && this.attr) {
         return `${min} - ${max} ${this.attr.currencies[currency]}`;
      }
      return '';
   }

   get languages(): { id: string; lang: string }[] {
      const lang_1 = this.jobManager.form.controls['lng_1'].value;
      const lang_2 = this.jobManager.form.controls['lng_2'].value;
      const lang_skill_1 = this.jobManager.form.controls['lng_skill_1'].value;
      const lang_skill_2 = this.jobManager.form.controls['lng_skill_2'].value;
      const results = [];
      if (this.attr) {
         if (lang_1 && lang_skill_1) {
            const lang = this.attr.langs.find((i) => i.id === Number(lang_1));
            const langSkill = this.attr.lang_skills.find((i) => i.id === Number(lang_skill_1));
            results.push({
               id: uuidv4(),
               lang: `${lang?.name} - ${langSkill?.name}`,
            });
         }

         if (lang_2 && lang_skill_2) {
            const lang = this.attr.langs.find((i) => i.id === Number(lang_2));
            const langSkill = this.attr.lang_skills.find((i) => i.id === Number(lang_skill_2));
            results.push({
               id: uuidv4(),
               lang: `${lang?.name} - ${langSkill?.name}`,
            });
         }
      }

      return results;
   }
}
